import { EpisodeSummarized } from "@/api/episodes-types";
import { RoleName } from "@/api/users-types";
import { EpisodeFilters } from "@/components/episodes/EpisodeFilters";
import { ImagesSummary } from "@/components/episodes/ImagesSummary";
import { MasksSummary } from "@/components/episodes/MasksSummary";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { Link } from "@/components/ui/Link";
import { AlertSnackbar, ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { EpisodeStateChip } from "@/components/ui/state-chips/EpisodeStateChip";
import { TagsCell } from "@/components/ui/tags/TagsCell";
import { TagsDropdown } from "@/components/ui/tags/TagsDropdown";
import { hasRole } from "@/hooks/use-has-role";
import { useRowCount } from "@/hooks/use-row-count";
import { useUser } from "@/queries/auth";
import { useEpisodes } from "@/queries/episodes/use-episodes";
import { useAddTag } from "@/queries/episodes/use-tags";
import { useAssignUserToEpisodes } from "@/queries/validation/use-assign-user-to-episodes";
import { useStoriesDatagridStore } from "@/stores/stories-datagrid-store";
import { useTagModalStore } from "@/stores/tag-modal-store";
import {
  episodeStateOptions,
  genderOptions,
  storyTypeOptions,
  timestampColumns,
} from "@/utils/datagrid-utils";
import { ChecklistRtlOutlined } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

function getColumns(
  onAddTag: (episodeIds: string[], tag: string) => void,
): GridColDef<EpisodeSummarized>[] {
  return [
    {
      field: "id",
      headerName: "ID",
      width: 120,
      filterable: false,
      renderCell: (params) => (
        <Link
          onClick={(e) => {
            e.stopPropagation(); // to prevent row selection when clicking the link
          }}
          to={`/episodes/${params.row.id}`}
        >
          {params.row.id}
        </Link>
      ),
    },
    {
      field: "story",
      headerName: "Story Type",
      width: 120,
      type: "singleSelect",
      valueOptions: storyTypeOptions,
      filterable: false,
    },
    {
      field: "category",
      headerName: "Category",
      width: 120,
      filterable: false,
    },
    {
      field: "subcategory",
      headerName: "Subcategory",
      width: 120,
      filterable: false,
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 90,
      type: "singleSelect",
      valueOptions: genderOptions,
      filterable: false,
    },
    {
      field: "modelName",
      headerName: "LLM Model Name",
      width: 150,
      filterable: false,
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      renderCell: (params) => {
        return <EpisodeStateChip state={params.row.state} />;
      },
      align: "center",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: episodeStateOptions,
      filterable: false,
    },
    {
      field: "visualsAssignee.username",
      headerName: "Assignee",
      sortable: false,
      filterable: false,
      width: 120,
      valueGetter: (_, row) => row.visualsAssignee?.username,
    },
    {
      field: "imagesSummary",
      headerName: "Images",
      sortable: false,
      filterable: false,
      width: 160,
      renderCell: (params) => (
        <ImagesSummary summary={params.row.imagesSummary} />
      ),
    },
    {
      field: "masksSummary",
      headerName: "Masks",
      sortable: false,
      filterable: false,
      width: 160,
      renderCell: (params) => (
        <MasksSummary summary={params.row.masksSummary} />
      ),
    },
    {
      field: "prompt",
      headerName: "Text Prompt",
      width: 180,
      valueGetter: (_, row) =>
        row.prompt ? `${row.prompt.name} (rev ${row.prompt.revision})` : "",
    },
    {
      field: "imagePrompt",
      headerName: "Image Prompt",
      width: 180,
      valueGetter: (_, row) =>
        row.imagePrompt
          ? `${row.imagePrompt.name} (rev ${row.imagePrompt.revision})`
          : "",
    },
    {
      field: "tags",
      headerName: "Tags",
      width: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TagsCell
          params={params}
          onAddTag={(episodeIds, tag) => onAddTag(episodeIds, tag)}
        />
      ),
    },
    {
      field: "hasManualEditImages",
      headerName: "Manual Edit",
      width: 120,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        return params.row.hasManualEditImages ? "✏️" : "";
      },
    },
    ...timestampColumns<EpisodeSummarized>(),
  ];
}

export function EpisodeListPage() {
  const {
    paginationModel,
    sortModel,
    selectedIds,
    filterItems,
    setPaginationModel,
    setSortModel,
    setSelectedIds,
    setFilterItems,
  } = useStoriesDatagridStore();

  const [assignUserMessage, setAssignUserMessage] = useState<string>();

  const { data, isLoading, error } = useEpisodes({
    paginationModel,
    sortModel,
    filterModel: {
      items: filterItems,
    },
  });
  const {
    mutate: addTag,
    error: addTagError,
    isPending: isAddTagPending,
  } = useAddTag();

  const { isTagRemovePending } = useTagModalStore();

  const rowCount = useRowCount(data);

  const onAddTag = useCallback(
    (episodeIds: string[], tag: string) => {
      addTag(
        { episodeIds, tag },
        {
          onSuccess: () => {
            setSelectedIds([]);
          },
        },
      );
    },
    [addTag, setSelectedIds],
  );

  const { mutate: assignUserToEpisodes, error: assignUserToEpisodesError } =
    useAssignUserToEpisodes();

  const onAssign = () => {
    const episodesToAssign = selectedIds.filter((episodeId) => {
      const episode = data?.items.find((e) => e.id === episodeId);
      return !episode?.visualsAssignee;
    });
    assignUserToEpisodes(
      { episodeIds: episodesToAssign },
      {
        onSuccess: () => {
          const alreadyAssigned = selectedIds.length - episodesToAssign.length;
          const assignedMsg = `${episodesToAssign.length} episodes assigned.`;
          const skippedMsg = `${alreadyAssigned} episodes skipped (already assigned).`;
          setSelectedIds([]);
          setAssignUserMessage(
            alreadyAssigned ? `${assignedMsg} ${skippedMsg}` : assignedMsg,
          );
        },
      },
    );
  };

  const snackbarOnClose = useCallback(() => {
    setAssignUserMessage(undefined);
  }, [setAssignUserMessage]);

  const columns = useMemo(() => getColumns(onAddTag), [onAddTag]);

  return (
    <Stack gap={2} mb={2}>
      <EpisodeFilters items={filterItems} onChange={setFilterItems} />
      <ApiErrorAlert error={error} />
      <ApiErrorSnackbar error={assignUserToEpisodesError} />
      <ApiErrorSnackbar error={addTagError} />
      <AlertSnackbar severity="info" onClose={snackbarOnClose}>
        {assignUserMessage}
      </AlertSnackbar>
      <DataGrid
        rowHeight={46}
        columnHeaderHeight={46}
        checkboxSelection
        columns={columns}
        loading={isLoading || isAddTagPending || isTagRemovePending}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode={"server"}
        paginationModel={paginationModel}
        sortingMode="server"
        sortModel={sortModel}
        rowCount={rowCount}
        rows={data?.items}
        rowSelectionModel={selectedIds}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={setSortModel}
        onRowSelectionModelChange={(model) => {
          setSelectedIds(model.map(String));
        }}
      />
      <Actions
        onAssign={onAssign}
        onAddTag={(tag) => {
          onAddTag(selectedIds, tag);
        }}
        tagsUpdatePending={isAddTagPending || isTagRemovePending}
      />
    </Stack>
  );
}

function Actions({
  tagsUpdatePending,
  onAssign,
  onAddTag,
}: {
  tagsUpdatePending: boolean;
  onAssign: () => void;
  onAddTag: (tag: string) => void;
}) {
  const { data: user } = useUser();
  const navigate = useNavigate();

  return (
    <>
      {user && hasRole(user, RoleName.visualsValidator) && (
        <Stack
          direction={"row"}
          gap={1}
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <TagsDropdown
            variant={"contained"}
            onChange={onAddTag}
            label="Add tag to selected"
            disabled={tagsUpdatePending}
          />
          <Button
            variant="contained"
            onClick={onAssign}
            sx={{ textWrap: "nowrap", flexShrink: 0 }}
          >
            Assign selected to me
          </Button>
          <Button
            variant="contained"
            startIcon={<ChecklistRtlOutlined />}
            onClick={() => navigate("/validate")}
            sx={{ textWrap: "nowrap", flexShrink: 0 }}
          >
            Start validating
          </Button>
        </Stack>
      )}
    </>
  );
}
